/**
 * Staggered Statistics Component
 * Orchestrates multiple statistics with staggered reveal animations
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, stagger, useAnimate } from 'framer-motion';
import { 
  useScrollAnimation, 
  useAnimationConfig, 
  useReducedMotion, 
  usePerformanceMonitor,
  StaggeredList
} from '../lib/animations';
import { useMultiCountingAnimation } from '../lib/animations/useCountingAnimation';
import AnimatedIcon, { IconAnimationConfig, iconAnimationPresets } from './AnimatedIcon';
import AnimatedProgressBar from './AnimatedProgressBar';
import { Home, Award, Users, Calendar, TrendingUp, Star, CheckCircle, Target, Zap, Heart } from 'lucide-react';

// Extended statistic item with stagger-specific properties
export interface StaggeredStatisticItem {
  id: string;
  label: string;
  value: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  icon?: React.ComponentType<any>;
  color?: string;
  description?: string;
  category?: string;
  priority?: number; // For custom stagger ordering
  animationDelay?: number; // Override default stagger delay
  showProgressBar?: boolean;
  maxValue?: number;
  iconAnimation?: IconAnimationConfig;
  customAnimation?: 'slideUp' | 'slideDown' | 'slideLeft' | 'slideRight' | 'fade' | 'scale' | 'rotate';
}

// Stagger configuration
export interface StaggerConfig {
  direction: 'sequential' | 'reverse' | 'center-out' | 'random' | 'by-category' | 'by-priority';
  baseDelay: number;
  staggerDelay: number;
  animationDuration: number;
  entranceAnimation: 'slideUp' | 'slideDown' | 'slideLeft' | 'slideRight' | 'fade' | 'scale' | 'rotate';
  showConnections?: boolean; // Visual connections between related stats
  groupByCategory?: boolean;
  highlightOnComplete?: boolean;
  cascadeEffect?: boolean; // Each completion triggers the next
}

interface StaggeredStatisticsProps {
  statistics: StaggeredStatisticItem[];
  config: StaggerConfig;
  title?: string;
  subtitle?: string;
  className?: string;
  triggerOnScroll?: boolean;
  threshold?: number;
  layout?: 'grid' | 'masonry' | 'timeline' | 'circular';
  onAllComplete?: () => void;
  onItemComplete?: (item: StaggeredStatisticItem) => void;
}

// Default configuration
const defaultConfig: StaggerConfig = {
  direction: 'sequential',
  baseDelay: 0.2,
  staggerDelay: 0.3,
  animationDuration: 0.8,
  entranceAnimation: 'slideUp',
  showConnections: false,
  groupByCategory: false,
  highlightOnComplete: true,
  cascadeEffect: false
};

// Animation variants for different entrance types
const createEntranceVariants = (type: string, index: number) => {
  const baseVariants = {
    slideUp: {
      hidden: { opacity: 0, y: 50, scale: 0.9 },
      visible: { opacity: 1, y: 0, scale: 1 }
    },
    slideDown: {
      hidden: { opacity: 0, y: -50, scale: 0.9 },
      visible: { opacity: 1, y: 0, scale: 1 }
    },
    slideLeft: {
      hidden: { opacity: 0, x: 50, scale: 0.9 },
      visible: { opacity: 1, x: 0, scale: 1 }
    },
    slideRight: {
      hidden: { opacity: 0, x: -50, scale: 0.9 },
      visible: { opacity: 1, x: 0, scale: 1 }
    },
    fade: {
      hidden: { opacity: 0 },
      visible: { opacity: 1 }
    },
    scale: {
      hidden: { opacity: 0, scale: 0.5 },
      visible: { opacity: 1, scale: 1 }
    },
    rotate: {
      hidden: { opacity: 0, rotate: -180, scale: 0.5 },
      visible: { opacity: 1, rotate: 0, scale: 1 }
    }
  };

  return baseVariants[type as keyof typeof baseVariants] || baseVariants.slideUp;
};

// Individual statistic card component
interface StatisticCardProps {
  statistic: StaggeredStatisticItem;
  index: number;
  isVisible: boolean;
  animationDelay: number;
  config: StaggerConfig;
  onComplete?: () => void;
}

const StatisticCard: React.FC<StatisticCardProps> = ({
  statistic,
  index,
  isVisible,
  animationDelay,
  config,
  onComplete
}) => {
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const [counterValue, setCounterValue] = useState(0);
  const animationConfig = useAnimationConfig();
  const isReducedMotion = useReducedMotion();
  const { trackAnimation } = usePerformanceMonitor();

  // Create entrance variants
  const variants = createEntranceVariants(
    statistic.customAnimation || config.entranceAnimation,
    index
  );

  // Icon component
  const IconComponent = statistic.icon;

  // Handle counter animation
  const handleCounterUpdate = (value: number) => {
    setCounterValue(value);
  };

  const handleCounterComplete = () => {
    setIsAnimationComplete(true);
    if (onComplete) onComplete();
  };

  // Format counter value
  const formatValue = (value: number) => {
    return `${statistic.prefix || ''}${value.toFixed(statistic.decimals || 0)}${statistic.suffix || ''}`;
  };

  return (
    <motion.div
      className="relative bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
      variants={variants}
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      transition={{
        duration: config.animationDuration,
        delay: animationDelay,
        ease: animationConfig.customTimings.defaultEasing
      }}
      onAnimationStart={() => trackAnimation.start()}
      onAnimationComplete={() => trackAnimation.end()}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
    >
      {/* Category Badge */}
      {statistic.category && (
        <motion.div
          className="absolute -top-2 -right-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs px-2 py-1 rounded-full"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: animationDelay + 0.3, duration: 0.5 }}
        >
          {statistic.category}
        </motion.div>
      )}

      {/* Icon with Animation */}
      {IconComponent && (
        <div className="mb-4">
          <AnimatedIcon
            icon={IconComponent}
            config={{
              ...iconAnimationPresets.countingSync,
              ...statistic.iconAnimation,
              counterValue,
              counterTarget: statistic.value,
              delay: animationDelay + 0.2
            }}
            size={32}
            color={statistic.color || '#3b82f6'}
            backgroundColor={`${statistic.color || '#3b82f6'}20`}
            borderRadius={8}
            padding={8}
          />
        </div>
      )}

      {/* Counter Display */}
      <motion.div
        className="mb-2"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: animationDelay + 0.1, duration: 0.5 }}
      >
        <motion.span
          className="text-3xl font-bold"
          style={{ color: statistic.color || '#1f2937' }}
          animate={isAnimationComplete && config.highlightOnComplete ? {
            scale: [1, 1.1, 1],
            color: [statistic.color || '#1f2937', '#10b981', statistic.color || '#1f2937']
          } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {formatValue(counterValue)}
        </motion.span>
      </motion.div>

      {/* Label */}
      <motion.h3
        className="text-lg font-semibold text-gray-900 mb-1"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: animationDelay + 0.2, duration: 0.4 }}
      >
        {statistic.label}
      </motion.h3>

      {/* Description */}
      {statistic.description && (
        <motion.p
          className="text-sm text-gray-600 mb-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: animationDelay + 0.3, duration: 0.4 }}
        >
          {statistic.description}
        </motion.p>
      )}

      {/* Progress Bar */}
      {statistic.showProgressBar && statistic.maxValue && (
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ delay: animationDelay + 0.4, duration: 0.5 }}
        >
          <AnimatedProgressBar
            config={{
              value: counterValue,
              maxValue: statistic.maxValue,
              height: 6,
              fillColor: statistic.color || '#3b82f6',
              animationDuration: 1,
              animationDelay: 0.2,
              showLabel: false
            }}
            triggerOnScroll={false}
          />
        </motion.div>
      )}

      {/* Completion Indicator */}
      <AnimatePresence>
        {isAnimationComplete && config.highlightOnComplete && (
          <motion.div
            className="absolute inset-0 rounded-xl pointer-events-none"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: [0, 0.3, 0], scale: [0.8, 1.1, 1] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            style={{
              background: `linear-gradient(135deg, ${statistic.color || '#3b82f6'}20, transparent)`,
              border: `2px solid ${statistic.color || '#3b82f6'}40`
            }}
          />
        )}
      </AnimatePresence>

      {/* Hidden counter for animation */}
      <div className="sr-only">
        <motion.span
          animate={{ opacity: isVisible ? 1 : 0 }}
          transition={{ delay: animationDelay + 0.5 }}
          onAnimationComplete={() => {
            // Start counter animation
            let startTime: number;
            const duration = config.animationDuration * 1000;
            
            const animateCounter = (timestamp: number) => {
              if (!startTime) startTime = timestamp;
              const elapsed = timestamp - startTime;
              const progress = Math.min(elapsed / duration, 1);
              
              // Easing function
              const easeOutQuart = 1 - Math.pow(1 - progress, 4);
              const currentValue = statistic.value * easeOutQuart;
              
              handleCounterUpdate(currentValue);
              
              if (progress < 1) {
                requestAnimationFrame(animateCounter);
              } else {
                handleCounterComplete();
              }
            };
            
            if (!isReducedMotion) {
              requestAnimationFrame(animateCounter);
            } else {
              handleCounterUpdate(statistic.value);
              handleCounterComplete();
            }
          }}
        />
      </div>
    </motion.div>
  );
};

// Main component
export const StaggeredStatistics: React.FC<StaggeredStatisticsProps> = ({
  statistics,
  config: userConfig,
  title,
  subtitle,
  className = '',
  triggerOnScroll = true,
  threshold = 0.1,
  layout = 'grid',
  onAllComplete,
  onItemComplete
}) => {
  const config = { ...defaultConfig, ...userConfig };
  const [completedItems, setCompletedItems] = useState<Set<string>>(new Set());
  const [visibleItems, setVisibleItems] = useState<Set<string>>(new Set());
  const scrollAnimation = useScrollAnimation(threshold, true);
  const isVisible = triggerOnScroll ? scrollAnimation.isVisible : true;
  const animationConfig = useAnimationConfig();
  const isReducedMotion = useReducedMotion();

  // Calculate stagger delays based on configuration
  const calculateStaggerDelays = (): Map<string, number> => {
    const delays = new Map<string, number>();
    let sortedStats = [...statistics];

    // Sort based on direction
    switch (config.direction) {
      case 'reverse':
        sortedStats.reverse();
        break;
      case 'center-out':
        const center = Math.floor(sortedStats.length / 2);
        const reordered = [];
        for (let i = 0; i < sortedStats.length; i++) {
          if (i % 2 === 0) {
            reordered.push(sortedStats[center + Math.floor(i / 2)]);
          } else {
            reordered.push(sortedStats[center - Math.ceil(i / 2)]);
          }
        }
        sortedStats = reordered.filter(Boolean);
        break;
      case 'random':
        sortedStats.sort(() => Math.random() - 0.5);
        break;
      case 'by-category':
        sortedStats.sort((a, b) => (a.category || '').localeCompare(b.category || ''));
        break;
      case 'by-priority':
        sortedStats.sort((a, b) => (b.priority || 0) - (a.priority || 0));
        break;
      default: // sequential
        break;
    }

    // Calculate delays
    sortedStats.forEach((stat, index) => {
      const baseDelay = config.baseDelay;
      const staggerDelay = stat.animationDelay || (index * config.staggerDelay);
      delays.set(stat.id, baseDelay + staggerDelay);
    });

    return delays;
  };

  const staggerDelays = calculateStaggerDelays();

  // Handle item completion
  const handleItemComplete = (statistic: StaggeredStatisticItem) => {
    setCompletedItems(prev => {
      const newSet = new Set(prev);
      newSet.add(statistic.id);
      
      if (onItemComplete) {
        onItemComplete(statistic);
      }
      
      // Check if all items are complete
      if (newSet.size === statistics.length && onAllComplete) {
        setTimeout(onAllComplete, 100);
      }
      
      return newSet;
    });
  };

  // Layout classes
  const layoutClasses = {
    grid: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6',
    masonry: 'columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6',
    timeline: 'flex flex-col space-y-6 max-w-2xl mx-auto',
    circular: 'flex flex-wrap justify-center items-center gap-8'
  };

  return (
    <div 
      ref={triggerOnScroll ? scrollAnimation.ref : undefined}
      className={`w-full ${className}`}
    >
      {/* Title and Subtitle */}
      {(title || subtitle) && (
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : -30 }}
          transition={{
            duration: animationConfig.customTimings.normal,
            ease: animationConfig.customTimings.defaultEasing
          }}
        >
          {title && (
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {subtitle}
            </p>
          )}
        </motion.div>
      )}

      {/* Statistics Container */}
      <div className={layoutClasses[layout]}>
        {statistics.map((statistic, index) => (
          <StatisticCard
            key={statistic.id}
            statistic={statistic}
            index={index}
            isVisible={isVisible}
            animationDelay={staggerDelays.get(statistic.id) || 0}
            config={config}
            onComplete={() => handleItemComplete(statistic)}
          />
        ))}
      </div>

      {/* Completion Celebration */}
      <AnimatePresence>
        {completedItems.size === statistics.length && (
          <motion.div
            className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              className="text-6xl"
              animate={{
                scale: [0, 1.2, 1],
                rotate: [0, 360, 0]
              }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              🎉
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Preset configurations
export const staggerPresets = {
  companyShowcase: {
    direction: 'sequential' as const,
    baseDelay: 0.3,
    staggerDelay: 0.4,
    animationDuration: 1.2,
    entranceAnimation: 'slideUp' as const,
    highlightOnComplete: true,
    cascadeEffect: true
  },
  
  achievementUnlock: {
    direction: 'center-out' as const,
    baseDelay: 0.2,
    staggerDelay: 0.3,
    animationDuration: 0.8,
    entranceAnimation: 'scale' as const,
    highlightOnComplete: true,
    showConnections: true
  },
  
  performanceMetrics: {
    direction: 'by-priority' as const,
    baseDelay: 0.1,
    staggerDelay: 0.2,
    animationDuration: 1.5,
    entranceAnimation: 'slideLeft' as const,
    highlightOnComplete: false,
    cascadeEffect: false
  }
};

export default StaggeredStatistics;